import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ProspectDealRolesCard } from '../components/DealContacts';
import { retainerSummary } from '../components/DealRetainer';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  InfoTooltip,
  Input,
  Loading,
  Modal,
  Select,
  StatusBadge,
  Textarea,
  Tooltip,
  cx,
  EmailStatusBadge,
} from '../components/ui';
import {
  CALL_OUTCOMES,
  INTERACTION_TYPES,
  api,
  type Activity,
  type CallOutcome,
  type ClaudeAnalysis,
  type ContactAlias,
  type Deal,
  type ProspectDealRole,
  type Interaction,
  type InteractionType,
  type LifecycleStage,
  type Outcome,
  type PlannedTouch,
  type Prospect,
  type Reply,
  type TimelineEvent,
} from '../lib/api';
import {
  channelLabel,
  date,
  dateOnly,
  dateTime,
  humanize,
  money,
  moneyIn,
  percent,
  relative,
} from '../lib/format';
import { useDraftApproval } from '../lib/draftApproval';
import { useAction, useApi } from '../lib/hooks';

interface DetailResponse {
  prospect: Prospect;
  activities: Activity[];
  replies: Reply[];
  outcome: Outcome | null;
  deals: Deal[];
  deal_roles?: ProspectDealRole[];
  interactions: Interaction[];
  sequence: PlannedTouch[];
  timeline: TimelineEvent[];
}

export function ProspectDetail() {
  const { id } = useParams<{ id: string }>();
  const detail = useApi<DetailResponse>(id ? `/api/prospects/${id}` : null);
  const [showOutcome, setShowOutcome] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [showSent, setShowSent] = useState(false);

  if (detail.loading) return <Loading />;
  if (detail.error) return <ErrorNote message={detail.error} onRetry={detail.reload} />;
  if (!detail.data) return null;

  // `deals` defaults for safety: a cached response from before this field
  // existed would otherwise crash the page on .length.
  const { prospect, activities, replies, outcome, sequence } = detail.data;
  const deals = detail.data.deals ?? [];
  const interactions = detail.data.interactions ?? [];
  const timeline = detail.data.timeline ?? [];
  const name = `${prospect.first_name} ${prospect.last_name}`.trim() || 'Unnamed prospect';

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/prospects" className="text-xs font-medium text-accent-600 hover:underline">
            ← Prospects
          </Link>
          <h1 className="mt-1 text-xl font-semibold tracking-tight text-ink-900">{name}</h1>
          <p className="mt-0.5 text-sm text-ink-600">
            {[prospect.job_title, prospect.company_name].filter(Boolean).join(' at ') || 'No title recorded'}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusBadge status={prospect.status} />
            {prospect.campaign_name && <Badge tone="blue">{prospect.campaign_name}</Badge>}
            {prospect.persona_name && <Badge tone="purple">{prospect.persona_name}</Badge>}
            <Badge>{humanize(prospect.source)}</Badge>
            {prospect.deal_value && <Badge tone="green">{money(prospect.deal_value)}</Badge>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setShowLog(true)}>Log call / meeting</Button>
          <Button onClick={() => setShowSent(true)}>Log a sent email</Button>
          <Button onClick={() => setShowReply(true)}>Log a reply</Button>
          <Button onClick={() => setShowOutcome(true)}>Mark won / lost</Button>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          <SummaryPanel prospect={prospect} onChanged={detail.reload} />

          <DraftPanel prospect={prospect} onDrafted={detail.reload} />

          <Card title={`Timeline (${timeline.length})`} bodyClassName={timeline.length ? 'p-0' : undefined}>
            {timeline.length === 0 ? (
              <p className="text-sm text-ink-500">
                Nothing on record yet. Sends, replies, notes and signals will appear here as they
                happen.
              </p>
            ) : (
              <ul className="divide-y divide-ink-100">
                {timeline.map((event) => (
                  <TimelineItem key={event.id} event={event} />
                ))}
              </ul>
            )}
          </Card>

          <Card title={`Replies (${replies.length})`} bodyClassName={replies.length ? 'p-0' : undefined}>
            {replies.length === 0 ? (
              <p className="text-sm text-ink-500">
                No replies yet. Gmail sync captures them automatically; you can also paste one in.
              </p>
            ) : (
              <ul className="divide-y divide-ink-100">
                {replies.map((reply) => (
                  <ReplyItem key={reply.id} reply={reply} onChanged={detail.reload} />
                ))}
              </ul>
            )}
          </Card>

          <Card
            title={`Calls, meetings & notes (${interactions.length})`}
            bodyClassName={interactions.length ? 'p-0' : undefined}
            action={
              <Button size="sm" onClick={() => setShowLog(true)}>
                Log one
              </Button>
            }
          >
            {interactions.length === 0 ? (
              <p className="text-sm text-ink-500">
                Nothing logged by hand yet. Record a call, a meeting or a note so the conversation
                that actually moves the deal is on the record too.
              </p>
            ) : (
              <ul className="divide-y divide-ink-100">
                {interactions.map((interaction) => (
                  <InteractionItem
                    key={interaction.id}
                    interaction={interaction}
                    onChanged={detail.reload}
                  />
                ))}
              </ul>
            )}
          </Card>

          <Card title="Activity timeline" bodyClassName={activities.length ? 'p-0' : undefined}>
            {activities.length === 0 ? (
              <EmptyState title="No touches yet" description="Draft the first message above." />
            ) : (
              <ul className="divide-y divide-ink-100">
                {activities.map((activity) => (
                  <ActivityItem key={activity.id} activity={activity} onChanged={detail.reload} />
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <Card title="Details">
            <div className="mb-3">
              <ApolloEnrichButton prospect={prospect} onEnriched={detail.reload} />
            </div>
            <dl className="space-y-2.5 text-sm">
              <DetailRow label="Email">
                {prospect.email ? (
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <a href={`mailto:${prospect.email}`} className="text-accent-700 hover:underline">
                      {prospect.email}
                    </a>
                    <EmailStatusBadge status={prospect.email_status} />
                  </span>
                ) : (
                  '—'
                )}
              </DetailRow>
              <DetailRow label="LinkedIn">
                {prospect.linkedin_url ? (
                  <a
                    href={prospect.linkedin_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent-700 hover:underline"
                  >
                    View profile
                  </a>
                ) : (
                  '—'
                )}
              </DetailRow>
              <DetailRow label="Industry">{prospect.company_industry || '—'}</DetailRow>
              <DetailRow label="Deal value">{money(prospect.deal_value)}</DetailRow>
              <DetailRow label="Added">{date(prospect.created_at)}</DetailRow>
              <DetailRow label="Last touch">
                {prospect.last_touch_at ? relative(prospect.last_touch_at) : '—'}
              </DetailRow>
              <DetailRow label="Timezone" tip="Scheduled sends land in this zone's morning. Edit it below if the AI guessed wrong.">
                {prospect.timezone ?? 'unknown'}
              </DetailRow>
              {Object.entries(prospect.custom_fields ?? {}).map(([key, value]) => (
                <DetailRow key={key} label={key}>
                  {String(value)}
                </DetailRow>
              ))}
            </dl>
            <LifecycleStageControl prospect={prospect} onChanged={detail.reload} />
            <TimezoneEditor prospect={prospect} onChanged={detail.reload} />
            <AliasEditor prospect={prospect} />
            <NotesEditor prospect={prospect} onSaved={detail.reload} />
          </Card>

          <Card
            title="Deals"
            action={
              <Link to="/pipeline" className="text-xs font-medium text-accent-700 underline underline-offset-2">
                Pipeline
              </Link>
            }
          >
            {deals.length === 0 ? (
              <p className="text-sm text-ink-500">
                No deals yet. Create one from the pipeline board to track this opportunity through to
                won or lost.
              </p>
            ) : (
              <ul className="grid gap-2">
                {deals.map((deal) => (
                  <li
                    key={deal.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-ink-50 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink-900">
                        {deal.name || 'Untitled deal'}
                      </p>
                      <p className="text-xs text-ink-500">
                        {moneyIn(deal.value, deal.currency)}
                        {deal.expected_close_date && ` · close ${dateOnly(deal.expected_close_date)}`}
                        {retainerSummary(deal) && ` · ${retainerSummary(deal)}`}
                      </p>
                    </div>
                    <StatusBadge status={deal.stage} />
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Roles on other deals">
            <ProspectDealRolesCard prospectId={prospect.id} roles={detail.data.deal_roles ?? []} onChanged={detail.reload} />
          </Card>

          {outcome && (
            <Card title="Outcome">
              <div className="flex items-center gap-2">
                <StatusBadge status={outcome.outcome} />
                {outcome.suggested_by_claude && <Badge tone="purple">Claude suggested</Badge>}
              </div>
              {outcome.reason && <p className="mt-2 text-sm text-ink-700">{outcome.reason}</p>}
              <p className="mt-2 text-xs text-ink-500">
                {money(outcome.deal_value)} · closed {date(outcome.closed_at)}
              </p>
              {deals.length > 0 && (
                <p className="mt-2 text-xs text-ink-400">
                  Derived from this prospect's deals — closing or reopening one updates it.
                </p>
              )}
            </Card>
          )}

          <Card title="Planned cadence">
            {prospect.sequence_paused_until && new Date(prospect.sequence_paused_until).getTime() > Date.now() && (
              <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-inset ring-amber-200">
                {prospect.sequence_paused_reason ?? 'Away'}: the sequence is held until{' '}
                {date(prospect.sequence_paused_until)}. Nothing new is drafted and scheduled emails wait.
                You can still send by hand.
              </p>
            )}
            {sequence.length === 0 ? (
              <p className="text-sm text-ink-500">
                This prospect's campaign has no sequence defined yet.
              </p>
            ) : (
              <ol className="space-y-2">
                {sequence.map((step) => (
                  <li key={step.step_order} className="flex items-start gap-2.5 text-sm">
                    <span
                      className={cx(
                        'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
                        step.status === 'sent' && 'bg-emerald-100 text-emerald-700',
                        step.status === 'drafted' && 'bg-amber-100 text-amber-700',
                        step.status === 'skipped' && 'bg-ink-100 text-ink-400',
                        step.status === 'pending' && 'bg-ink-100 text-ink-600',
                      )}
                    >
                      {step.step_order}
                    </span>
                    <div className="min-w-0">
                      <p className="text-ink-800">
                        Day {step.day_offset} · {channelLabel(step.channel)}
                      </p>
                      <p className="text-xs text-ink-500">
                        {humanize(step.message_type)} · {step.status}
                        {step.due_at && step.status === 'pending' ? ` · due ${date(step.due_at)}` : ''}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
      </div>

      <LogInteractionModal
        open={showLog}
        onClose={() => setShowLog(false)}
        prospectId={prospect.id}
        deals={deals}
        onLogged={() => {
          setShowLog(false);
          detail.reload();
        }}
      />

      <LogSentModal
        open={showSent}
        onClose={() => setShowSent(false)}
        prospectId={prospect.id}
        onDone={() => {
          setShowSent(false);
          detail.reload();
        }}
      />

      <OutcomeModal
        open={showOutcome}
        onClose={() => setShowOutcome(false)}
        prospect={prospect}
        onDone={() => {
          setShowOutcome(false);
          detail.reload();
        }}
      />

      <LogReplyModal
        open={showReply}
        onClose={() => setShowReply(false)}
        prospectId={prospect.id}
        onDone={() => {
          setShowReply(false);
          detail.reload();
        }}
      />
    </>
  );
}

function DetailRow({
  label,
  tip,
  children,
}: {
  label: string;
  tip?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="flex shrink-0 items-center gap-1 text-xs uppercase tracking-wide text-ink-500">
        {label}
        {tip && <InfoTooltip content={tip} side="bottom" />}
      </dt>
      <dd className="min-w-0 truncate text-right text-ink-800">{children}</dd>
    </div>
  );
}

const LIFECYCLE_STAGES: LifecycleStage[] = [
  'lead',
  'engaged',
  'qualified',
  'customer',
  'churned',
  'disqualified',
];

const LIFECYCLE_TONE: Record<LifecycleStage, 'neutral' | 'blue' | 'purple' | 'green' | 'amber'> = {
  lead: 'neutral',
  engaged: 'blue',
  qualified: 'purple',
  customer: 'green',
  churned: 'amber',
  disqualified: 'amber',
};

function LifecycleStageControl({ prospect, onChanged }: { prospect: Prospect; onChanged: () => void }) {
  const move = useAction(async (stage: LifecycleStage) => {
    if (stage === prospect.lifecycle_stage) return;
    await api.put(`/api/prospects/${prospect.id}/lifecycle-stage`, { stage });
    onChanged();
  });

  return (
    <div className="mt-4 border-t border-ink-100 pt-3">
      <Field label="Lifecycle stage">
        <div className="flex items-center gap-2">
          <Select
            value={prospect.lifecycle_stage}
            disabled={move.pending}
            onChange={(event) => void move.run(event.target.value as LifecycleStage)}
          >
            {LIFECYCLE_STAGES.map((stage) => (
              <option key={stage} value={stage}>
                {humanize(stage)}
              </option>
            ))}
          </Select>
          <Badge tone={LIFECYCLE_TONE[prospect.lifecycle_stage]}>{humanize(prospect.lifecycle_stage)}</Badge>
        </div>
      </Field>
      {move.error && <div className="mt-2"><ErrorNote message={move.error} /></div>}
    </div>
  );
}

const TIMELINE_KIND_LABEL: Record<TimelineEvent['kind'], string> = {
  email_out: 'Email sent',
  email_in: 'Email received',
  linkedin_dm: 'LinkedIn DM',
  linkedin_touch: 'LinkedIn touch',
  note: 'Note',
  call: 'Call',
  meeting: 'Meeting',
  signal: 'Signal',
  deal_stage: 'Deal stage',
  lifecycle_change: 'Stage change',
  system: 'System',
};

const TIMELINE_KIND_TONE: Record<TimelineEvent['kind'], 'neutral' | 'blue' | 'purple' | 'green' | 'amber'> = {
  email_out: 'blue',
  email_in: 'blue',
  linkedin_dm: 'purple',
  linkedin_touch: 'purple',
  note: 'neutral',
  call: 'amber',
  meeting: 'amber',
  signal: 'green',
  deal_stage: 'green',
  lifecycle_change: 'purple',
  system: 'neutral',
};

function TimelineItem({ event }: { event: TimelineEvent }) {
  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={TIMELINE_KIND_TONE[event.kind]}>{TIMELINE_KIND_LABEL[event.kind]}</Badge>
        <span className="text-sm font-medium text-ink-800">{event.title || TIMELINE_KIND_LABEL[event.kind]}</span>
        <span className="ml-auto text-xs text-ink-400" title={dateTime(event.occurred_at)}>
          {relative(event.occurred_at)}
        </span>
      </div>
      {event.body && <p className="mt-1.5 whitespace-pre-wrap text-sm text-ink-600">{event.body}</p>}
    </li>
  );
}

const TIMEZONE_SOURCE_LABEL: Record<string, string> = {
  ai_inferred: 'AI guess',
  csv_import: 'from CSV',
  manual: 'set manually',
};

const IANA_TIMEZONES: string[] =
  typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];

/**
 * Scheduled sends land in this timezone, so it is worth a correction when the
 * AI guessed wrong — a manual value is never overwritten by later AI passes.
 */
function TimezoneEditor({ prospect, onChanged }: { prospect: Prospect; onChanged: () => void }) {
  const [value, setValue] = useState(prospect.timezone ?? '');

  const save = useAction(async () => {
    await api.put(`/api/prospects/${prospect.id}`, { timezone: value.trim() });
    onChanged();
  });

  const redetect = useAction(async () => {
    const result = await api.post<{ prospect: Prospect; inferred: boolean }>(
      `/api/prospects/${prospect.id}/infer-timezone`,
    );
    setValue(result.prospect.timezone ?? '');
    onChanged();
  });

  return (
    <div className="mt-4 border-t border-ink-100 pt-3">
      <Field label="Timezone" hint="Scheduled emails send in this zone's morning.">
        <div className="flex gap-2">
          <Input
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder="Europe/Paris"
            list="iana-timezones"
            autoComplete="off"
          />
          <Button size="sm" loading={redetect.pending} onClick={() => void redetect.run()}>
            Detect
          </Button>
        </div>
      </Field>
      {IANA_TIMEZONES.length > 0 && (
        <datalist id="iana-timezones">
          {IANA_TIMEZONES.map((tz) => (
            <option key={tz} value={tz} />
          ))}
        </datalist>
      )}
      {prospect.timezone && prospect.timezone_source && (
        <p className="mt-1 text-xs text-ink-500">
          {TIMEZONE_SOURCE_LABEL[prospect.timezone_source] ?? prospect.timezone_source}
          {prospect.timezone_source === 'ai_inferred' && prospect.timezone_confidence
            ? ` · ${prospect.timezone_confidence} confidence`
            : ''}
        </p>
      )}
      {(save.error || redetect.error) && (
        <div className="mt-2"><ErrorNote message={(save.error ?? redetect.error) as string} /></div>
      )}
      {value.trim() !== (prospect.timezone ?? '') && value.trim() !== '' && (
        <Button size="sm" className="mt-2" loading={save.pending} onClick={() => void save.run()}>
          Save timezone
        </Button>
      )}
    </div>
  );
}

/**
 * Other addresses this person writes from. Mail to or from one lands on the
 * same timeline (the Gmail sync matches the primary address and these).
 */
function AliasEditor({ prospect }: { prospect: Prospect }) {
  const aliases = useApi<{ aliases: ContactAlias[] }>(`/api/prospects/${prospect.id}/aliases`);
  const [email, setEmail] = useState('');
  const add = useAction(async () => {
    await api.post(`/api/prospects/${prospect.id}/aliases`, { email });
    setEmail('');
    aliases.reload();
  });
  const remove = useAction(async (aliasId: string) => {
    await api.del(`/api/prospects/${prospect.id}/aliases/${aliasId}`);
    aliases.reload();
  });
  const list = aliases.data?.aliases ?? [];

  return (
    <div className="mt-4">
      <p className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">Other addresses</p>
      {list.length === 0 ? (
        <p className="text-xs text-ink-500">None. Add one so mail from a second address reaches this timeline.</p>
      ) : (
        <ul className="grid gap-1">
          {list.map((alias) => (
            <li key={alias.id} className="flex items-center justify-between gap-2 text-sm text-ink-800">
              {alias.email}
              <button
                type="button"
                aria-label={`Remove ${alias.email}`}
                className="text-ink-400 hover:text-ink-700"
                onClick={() => void remove.run(alias.id)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {(add.error || remove.error) && <div className="mt-1"><ErrorNote message={add.error ?? remove.error ?? ''} /></div>}
      <div className="mt-2 flex gap-2">
        <Input value={email} placeholder="name@other-address.com" onChange={(event) => setEmail(event.target.value)} />
        <Button size="sm" disabled={!email.trim()} loading={add.pending} onClick={() => void add.run()}>
          Add
        </Button>
      </div>
    </div>
  );
}

function NotesEditor({ prospect, onSaved }: { prospect: Prospect; onSaved: () => void }) {
  const [notes, setNotes] = useState(prospect.notes);
  const save = useAction(async () => {
    await api.put(`/api/prospects/${prospect.id}`, { notes });
    onSaved();
  });

  return (
    <div className="mt-4 border-t border-ink-100 pt-3">
      <Field label="Notes" hint="Claude reads these when drafting.">
        <Textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
      </Field>
      {save.error && <div className="mt-2"><ErrorNote message={save.error} /></div>}
      {notes !== prospect.notes && (
        <Button size="sm" className="mt-2" loading={save.pending} onClick={() => void save.run()}>
          Save notes
        </Button>
      )}
    </div>
  );
}

function ApolloEnrichButton({ prospect, onEnriched }: { prospect: Prospect; onEnriched: () => void }) {
  const enrich = useAction(async () => {
    await api.post(`/api/prospects/${prospect.id}/enrich-from-apollo`);
    onEnriched();
  });

  return (
    <div>
      <Tooltip content="Pulls fresh company and title data from Apollo into this prospect's fields. Read-only — nothing here is ever sent to Apollo.">
        <Button
          size="sm"
          variant="ghost"
          loading={enrich.pending}
          onClick={() => void enrich.run()}
          disabled={!prospect.email}
        >
          Enrich from Apollo
        </Button>
      </Tooltip>
      {enrich.error && <div className="mt-2"><ErrorNote message={enrich.error as string} /></div>}
    </div>
  );
}

/** Phase 4 of docs/CRM_ROADMAP.md: the top-of-record briefing, refreshed by the scheduler and on demand here. */
function SummaryPanel({ prospect, onChanged }: { prospect: Prospect; onChanged: () => void }) {
  const refresh = useAction(async () => {
    await api.post(`/api/prospects/${prospect.id}/refresh-summary`);
    onChanged();
  });

  const hasSummary = Boolean(prospect.summary);

  return (
    <Card
      title="Summary"
      action={
        <Button size="sm" loading={refresh.pending} onClick={() => void refresh.run()}>
          {hasSummary ? 'Refresh' : 'Summarize'}
        </Button>
      }
    >
      {!hasSummary ? (
        <p className="text-sm text-ink-500">
          No summary yet. It refreshes automatically as activity comes in, or generate one now.
        </p>
      ) : (
        <>
          <p className="text-sm text-ink-800">{prospect.summary}</p>
          {prospect.summary_next_step && (
            <div className="mt-2.5 rounded-md bg-accent-50 px-3 py-2 text-sm text-accent-900 ring-1 ring-inset ring-accent-200">
              <span className="font-medium">Next: </span>
              {prospect.summary_next_step}
              {prospect.next_step_date && <span className="ml-1 text-accent-700">({dateOnly(prospect.next_step_date)})</span>}
            </div>
          )}
          {prospect.summary_updated_at && (
            <p className="mt-2 text-xs text-ink-400">Updated {relative(prospect.summary_updated_at)}</p>
          )}
        </>
      )}
      {refresh.error && <div className="mt-2"><ErrorNote message={refresh.error} /></div>}
    </Card>
  );
}

function DraftPanel({ prospect, onDrafted }: { prospect: Prospect; onDrafted: () => void }) {
  const [channel, setChannel] = useState<'email' | 'linkedin_dm' | 'linkedin_post_engage'>('email');
  const [instructions, setInstructions] = useState('');

  const draft = useAction(async () => {
    await api.post('/api/outreach/drafts', {
      prospect_id: prospect.id,
      channel,
      instructions: instructions || undefined,
    });
    setInstructions('');
    onDrafted();
  });

  return (
    <Card title="Draft with Claude">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-52">
          <Field label="Channel">
            <Select value={channel} onChange={(event) => setChannel(event.target.value as typeof channel)}>
              <option value="email">Email</option>
              <option value="linkedin_dm">LinkedIn DM</option>
              <option value="linkedin_post_engage">LinkedIn post comment</option>
            </Select>
          </Field>
        </div>
        <div className="min-w-[16rem] flex-1">
          <Field label="Anything specific for this one?">
            <Input
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
              placeholder="Reference the RFP they posted last week."
            />
          </Field>
        </div>
        <Button variant="primary" loading={draft.pending} onClick={() => void draft.run()}>
          Draft {channel === 'email' ? 'email' : 'message'}
        </Button>
      </div>
      {draft.error && <div className="mt-3"><ErrorNote message={draft.error} /></div>}
      <p className="mt-3 text-xs text-ink-500">
        The draft appears in the timeline below, editable, and sends only when you approve it.
      </p>
    </Card>
  );
}

function ActivityItem({ activity, onChanged }: { activity: Activity; onChanged: () => void }) {
  const editable = activity.status !== 'sent' && activity.status !== 'skipped';
  const [subject, setSubject] = useState(activity.subject ?? '');
  const [body, setBody] = useState(activity.your_edits ?? activity.final_message ?? activity.claude_draft);
  const [manual, setManual] = useState<{ payload: string; url: string | null } | null>(null);

  const dirty = body !== (activity.your_edits ?? activity.claude_draft) || subject !== (activity.subject ?? '');

  // Same save → approve → send rule as the review queue, from one helper, so
  // a fresh draft can be approved and sent here in one click by an admin and
  // a non-admin sees the same "an admin has to approve" note as in the queue.
  const { isAdmin, approved, canSend, commitAndApprove, sendLabel } = useDraftApproval(activity, {
    subject,
    body,
    dirty,
  });

  const save = useAction(async () => {
    await api.put(`/api/outreach/${activity.id}`, { subject, body });
    onChanged();
  });

  const send = useAction(async () => {
    await commitAndApprove();
    const result = await api.post<{ manualPayload?: { payload: string; url: string | null } }>(
      `/api/outreach/${activity.id}/send`,
    );
    if (result.manualPayload) setManual(result.manualPayload);
    onChanged();
  });

  const skip = useAction(async () => {
    await api.post(`/api/outreach/${activity.id}/skip`);
    onChanged();
  });

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={activity.status} />
        <Badge>{channelLabel(activity.channel)}</Badge>
        <span className="text-sm text-ink-600">{humanize(activity.message_type)}</span>
        {activity.email_opened && (
          <Badge tone="green">opened{activity.email_open_count > 1 ? ` ×${activity.email_open_count}` : ''}</Badge>
        )}
        {activity.email_clicked && (
          <Badge tone="blue">clicked{activity.email_click_count > 1 ? ` ×${activity.email_click_count}` : ''}</Badge>
        )}
        <span className="ml-auto text-xs text-ink-400">
          {activity.sent_at ? `sent ${relative(activity.sent_at)}` : `drafted ${relative(activity.created_at)}`}
        </span>
      </div>

      {activity.error_message && (
        <div className="mt-2">
          <ErrorNote message={activity.error_message} />
        </div>
      )}

      {editable ? (
        <div className="mt-2.5 space-y-2">
          {activity.channel === 'email' && (
            <Input value={subject} onChange={(event) => setSubject(event.target.value)} placeholder="Subject" />
          )}
          <Textarea rows={8} value={body} onChange={(event) => setBody(event.target.value)} />
          {(save.error || send.error) && <ErrorNote message={(save.error ?? send.error) as string} />}
          {manual && (
            <div className="rounded-md bg-accent-50 p-3 text-sm text-accent-900 ring-1 ring-inset ring-accent-200">
              <p className="font-medium">Copy this into LinkedIn</p>
              <p className="mt-1 whitespace-pre-wrap text-ink-800">{manual.payload}</p>
              <div className="mt-2 flex gap-2">
                <Button size="sm" onClick={() => void navigator.clipboard.writeText(manual.payload)}>
                  Copy message
                </Button>
                {manual.url && (
                  <a
                    href={manual.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-xs font-medium text-ink-800 ring-1 ring-inset ring-ink-200 hover:bg-ink-50"
                  >
                    Open profile
                  </a>
                )}
              </div>
            </div>
          )}
          {approved && !dirty && (
            <p className="text-xs text-ink-500">
              Approved{activity.approved_by_name ? ` by ${activity.approved_by_name}` : ''}
              {activity.approved_at ? ` ${relative(activity.approved_at)}` : ''}.
            </p>
          )}
          {approved && dirty && (
            <p className="text-xs text-amber-700">
              Editing this message withdraws its approval
              {isAdmin ? ' — sending re-approves it.' : ', so an admin will need to approve it again.'}
            </p>
          )}
          {!approved && !isAdmin && (
            <p className="text-xs text-ink-500">An admin has to approve this before it can be sent.</p>
          )}
          <div className="flex flex-wrap gap-2">
            {canSend && (
              <Button variant="primary" size="sm" loading={send.pending} onClick={() => void send.run()}>
                {sendLabel}
              </Button>
            )}
            {dirty && (
              <Button size="sm" loading={save.pending} onClick={() => void save.run()}>
                Save edits
              </Button>
            )}
            <Button variant="ghost" size="sm" loading={skip.pending} onClick={() => void skip.run()}>
              Skip
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-2 rounded-md bg-ink-50 p-3 text-sm text-ink-700">
          {activity.subject && <p className="mb-1 font-medium text-ink-900">{activity.subject}</p>}
          <p className="whitespace-pre-wrap">{activity.final_message ?? activity.claude_draft}</p>
        </div>
      )}
    </li>
  );
}

function ReplyItem({ reply, onChanged }: { reply: Reply; onChanged: () => void }) {
  const analysis = reply.claude_analysis;

  const act = useAction(async (action: string, draftNow: boolean) => {
    await api.post(`/api/replies/${reply.id}/action`, { action, draft_now: draftNow });
    onChanged();
  });

  const reanalyze = useAction(async () => {
    await api.post(`/api/replies/${reply.id}/reanalyze`);
    onChanged();
  });

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={reply.intent} />
        <Badge>{channelLabel(reply.channel)}</Badge>
        {reply.sentiment_score && <Badge>sentiment {Number(reply.sentiment_score).toFixed(2)}</Badge>}
        {reply.your_action && <Badge tone="blue">{humanize(reply.your_action)}</Badge>}
        <span className="ml-auto text-xs text-ink-400">{dateTime(reply.received_at)}</span>
      </div>

      <p className="mt-2 whitespace-pre-wrap rounded-md bg-ink-50 p-3 text-sm text-ink-800">
        {reply.message_body}
      </p>

      {analysis ? (
        <AnalysisBlock analysis={analysis} confidence={reply.confidence_score} />
      ) : (
        <div className="mt-2 flex items-center gap-2 text-xs text-ink-500">
          Not analyzed yet.
          <Button size="sm" variant="ghost" loading={reanalyze.pending} onClick={() => void reanalyze.run()}>
            Analyze with Claude
          </Button>
        </div>
      )}

      {!reply.action_taken_at && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="primary"
            loading={act.pending}
            onClick={() => void act.run('follow_up', true)}
          >
            Follow up & draft
          </Button>
          <Tooltip content="Flags this reply for manual handling instead of an automated follow-up — for anything Claude's suggested next step isn't right for.">
            <Button size="sm" loading={act.pending} onClick={() => void act.run('escalate', false)}>
              Escalate
            </Button>
          </Tooltip>
          <Button size="sm" loading={act.pending} onClick={() => void act.run('close_won', false)}>
            Won
          </Button>
          <Button size="sm" variant="danger" loading={act.pending} onClick={() => void act.run('close_lost', false)}>
            Lost
          </Button>
        </div>
      )}
      {act.error && <div className="mt-2"><ErrorNote message={act.error} /></div>}
    </li>
  );
}

export function AnalysisBlock({
  analysis,
  confidence,
}: {
  analysis: ClaudeAnalysis;
  confidence?: string | null;
}) {
  const groups: Array<[string, string[]]> = [
    ['Objections', analysis.key_objections],
    ['Pain points', analysis.pain_points_mentioned],
    ['Budget', analysis.budget_signals],
    ['Timeline', analysis.timeline_signals],
  ];

  return (
    <div className="mt-2 rounded-md border border-accent-100 bg-accent-50/50 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-accent-800">Claude's read</p>
        {confidence && (
          <span className="text-xs text-ink-500">confidence {percent(Number(confidence))}</span>
        )}
      </div>
      <p className="mt-1 text-sm text-ink-800">{analysis.summary}</p>
      <p className="mt-1.5 text-sm font-medium text-accent-800">→ {analysis.recommended_next_step}</p>

      <div className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-2">
        {groups
          .filter(([, values]) => values?.length)
          .map(([label, values]) => (
            <div key={label} className="text-xs">
              <span className="font-medium text-ink-700">{label}: </span>
              <span className="text-ink-600">{values.join('; ')}</span>
            </div>
          ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Interactions: calls, meetings and notes logged by hand
// ---------------------------------------------------------------------------
const INTERACTION_TONE: Record<InteractionType, 'neutral' | 'blue' | 'purple'> = {
  note: 'neutral',
  call: 'blue',
  meeting: 'purple',
};

/** Only `connected` is a good outcome; the rest are worth showing as such. */
const CALL_OUTCOME_TONE: Record<CallOutcome, 'green' | 'amber' | 'neutral'> = {
  connected: 'green',
  voicemail: 'amber',
  no_answer: 'amber',
  wrong_number: 'neutral',
};

function InteractionItem({
  interaction,
  onChanged,
}: {
  interaction: Interaction;
  onChanged: () => void;
}) {
  const remove = useAction(async () => {
    await api.del(`/api/interactions/${interaction.id}`);
    onChanged();
  });

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={INTERACTION_TONE[interaction.type]}>{humanize(interaction.type)}</Badge>
        {interaction.call_outcome && (
          <Badge tone={CALL_OUTCOME_TONE[interaction.call_outcome]}>
            {humanize(interaction.call_outcome)}
          </Badge>
        )}
        {interaction.duration_minutes != null && (
          <span className="text-xs text-ink-500">{interaction.duration_minutes} min</span>
        )}
        {interaction.deal_name && <Badge tone="green">{interaction.deal_name}</Badge>}
        <span className="ml-auto text-xs text-ink-400">{dateTime(interaction.occurred_at)}</span>
      </div>

      {interaction.subject && (
        <p className="mt-1.5 text-sm font-medium text-ink-900">{interaction.subject}</p>
      )}
      {interaction.body && (
        <p className="mt-1 whitespace-pre-wrap text-sm text-ink-700">{interaction.body}</p>
      )}

      <div className="mt-1.5 flex items-center gap-2">
        <span className="text-xs text-ink-400">
          Logged by {interaction.created_by_name || 'someone since removed'}
        </span>
        <button
          onClick={() => void remove.run()}
          disabled={remove.pending}
          className="text-xs text-ink-400 underline underline-offset-2 hover:text-red-700 disabled:opacity-50"
        >
          Delete
        </button>
      </div>
      {remove.error && (
        <p className="mt-1.5 text-xs text-red-700">{remove.error}</p>
      )}
    </li>
  );
}

function LogInteractionModal({
  open,
  onClose,
  prospectId,
  deals,
  onLogged,
}: {
  open: boolean;
  onClose: () => void;
  prospectId: string;
  deals: Deal[];
  onLogged: () => void;
}) {
  const [type, setType] = useState<InteractionType>('call');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [duration, setDuration] = useState('');
  const [callOutcome, setCallOutcome] = useState<CallOutcome>('connected');
  const [dealId, setDealId] = useState('');
  const [occurredAt, setOccurredAt] = useState('');

  const save = useAction(async () => {
    await api.post('/api/interactions', {
      prospect_id: prospectId,
      type,
      subject,
      body,
      deal_id: dealId || null,
      duration_minutes: type === 'note' || !duration ? null : Number(duration),
      // The server rejects a call_outcome on anything but a call, so only send
      // it for one rather than relying on the caller to clear the field.
      call_outcome: type === 'call' ? callOutcome : null,
      occurred_at: occurredAt ? new Date(occurredAt).toISOString() : undefined,
    });
    setSubject('');
    setBody('');
    setDuration('');
    setOccurredAt('');
    setDealId('');
    onLogged();
  });

  return (
    <Modal open={open} onClose={onClose} title="Log a call, meeting or note">
      <div className="grid gap-3">
        {save.error && <ErrorNote message={save.error} />}

        <Field label="What happened">
          <Select value={type} onChange={(event) => setType(event.target.value as InteractionType)}>
            {INTERACTION_TYPES.map((option) => (
              <option key={option} value={option}>
                {humanize(option)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Summary" hint="One line — this is what the timeline shows.">
          <Input
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder={
              type === 'call'
                ? 'Discussed turnaround on the contract set'
                : type === 'meeting'
                  ? 'Scoping call with their GC'
                  : 'Introduced by a mutual contact'
            }
          />
        </Field>

        <Field label="Detail" hint="Optional.">
          <Textarea rows={4} value={body} onChange={(event) => setBody(event.target.value)} />
        </Field>

        {type !== 'note' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Duration (minutes)">
              <Input
                type="number"
                min="1"
                value={duration}
                onChange={(event) => setDuration(event.target.value)}
                placeholder="18"
              />
            </Field>
            {type === 'call' && (
              <Field label="Outcome">
                <Select
                  value={callOutcome}
                  onChange={(event) => setCallOutcome(event.target.value as CallOutcome)}
                >
                  {CALL_OUTCOMES.map((option) => (
                    <option key={option} value={option}>
                      {humanize(option)}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="When" hint="Leave blank for now. Backdate a call you are typing up later.">
            <Input
              type="datetime-local"
              value={occurredAt}
              onChange={(event) => setOccurredAt(event.target.value)}
            />
          </Field>
          {deals.length > 0 && (
            <Field label="Deal" hint="Optional — links this to one opportunity.">
              <Select value={dealId} onChange={(event) => setDealId(event.target.value)}>
                <option value="">Not deal-specific</option>
                {deals.map((deal) => (
                  <option key={deal.id} value={deal.id}>
                    {deal.name || 'Untitled deal'}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.pending} onClick={() => void save.run()}>
            Log it
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function OutcomeModal({
  open,
  onClose,
  prospect,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  prospect: Prospect;
  onDone: () => void;
}) {
  const [outcome, setOutcome] = useState<'won' | 'lost' | 'no_reply' | 'unqualified'>('won');
  const [reason, setReason] = useState('');
  const [dealValue, setDealValue] = useState(prospect.deal_value ?? '');

  const suggest = useAction(async () => {
    const response = await api.post<{
      suggestion: { outcome: typeof outcome; reason: string; confidence_score: number };
    }>('/api/outcomes/suggest', { prospect_id: prospect.id });
    setOutcome(response.suggestion.outcome);
    setReason(response.suggestion.reason);
  });

  const save = useAction(async () => {
    await api.post('/api/outcomes', {
      prospect_id: prospect.id,
      outcome,
      reason,
      deal_value: dealValue ? Number(dealValue) : null,
    });
    onDone();
  });

  return (
    <Modal open={open} onClose={onClose} title="Record the outcome">
      <div className="space-y-3">
        <Button loading={suggest.pending} onClick={() => void suggest.run()}>
          Ask Claude to classify this
        </Button>
        {suggest.error && <ErrorNote message={suggest.error} />}

        <Field label="Outcome">
          <Select value={outcome} onChange={(event) => setOutcome(event.target.value as typeof outcome)}>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            <option value="no_reply">No reply</option>
            <option value="unqualified">Unqualified</option>
          </Select>
        </Field>

        <Field label="Reason" hint="Feeds the win/loss analytics.">
          <Textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>

        {outcome === 'won' && (
          <Field label="Deal value (USD)">
            <Input type="number" value={dealValue} onChange={(event) => setDealValue(event.target.value)} />
          </Field>
        )}

        {save.error && <ErrorNote message={save.error} />}

        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.pending} onClick={() => void save.run()}>
            Save outcome
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Logs a message sent by hand outside the draft/approve flow — e.g. touching
 * base with an old client straight from Gmail — as a sent outreach_activity,
 * so it lands on the timeline and any reply against it has something to
 * link to.
 */
function LogSentModal({
  open,
  onClose,
  prospectId,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  prospectId: string;
  onDone: () => void;
}) {
  const [channel, setChannel] = useState<'email' | 'linkedin_dm'>('email');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const save = useAction(async () => {
    await api.post('/api/outreach/log', {
      prospect_id: prospectId,
      channel,
      subject: subject.trim() || undefined,
      body,
    });
    setSubject('');
    setBody('');
    onDone();
  });

  return (
    <Modal open={open} onClose={onClose} title="Log a sent email">
      <div className="space-y-3">
        <p className="text-sm text-ink-600">
          For a message you already sent outside the app — touching base with an old client,
          say. It's recorded as sent and shows up on the timeline; log their reply against it
          separately.
        </p>
        <Field label="Channel">
          <Select value={channel} onChange={(event) => setChannel(event.target.value as typeof channel)}>
            <option value="email">Email</option>
            <option value="linkedin_dm">LinkedIn DM</option>
          </Select>
        </Field>
        {channel === 'email' && (
          <Field label="Subject">
            <Input value={subject} onChange={(event) => setSubject(event.target.value)} />
          </Field>
        )}
        <Field label="What you sent">
          <Textarea rows={8} value={body} onChange={(event) => setBody(event.target.value)} />
        </Field>
        {save.error && <ErrorNote message={save.error} />}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.pending} disabled={!body.trim()} onClick={() => void save.run()}>
            Log it
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function LogReplyModal({
  open,
  onClose,
  prospectId,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  prospectId: string;
  onDone: () => void;
}) {
  const [channel, setChannel] = useState<'email' | 'linkedin_dm'>('email');
  const [body, setBody] = useState('');

  const save = useAction(async () => {
    await api.post('/api/replies', {
      prospect_id: prospectId,
      channel,
      message_body: body,
    });
    setBody('');
    onDone();
  });

  return (
    <Modal open={open} onClose={onClose} title="Log a reply">
      <div className="space-y-3">
        <p className="text-sm text-ink-600">
          Paste what they wrote. Claude analyses it for intent, objections, and the next step.
        </p>
        <Field label="Channel">
          <Select value={channel} onChange={(event) => setChannel(event.target.value as typeof channel)}>
            <option value="email">Email</option>
            <option value="linkedin_dm">LinkedIn DM</option>
          </Select>
        </Field>
        <Field label="Their message">
          <Textarea rows={8} value={body} onChange={(event) => setBody(event.target.value)} />
        </Field>
        {save.error && <ErrorNote message={save.error} />}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.pending} disabled={!body.trim()} onClick={() => void save.run()}>
            Save & analyze
          </Button>
        </div>
      </div>
    </Modal>
  );
}
